import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../config';

export default function LegalAcceptanceModal({ open, legal, onAccepted, onLogout }) {
  const { t } = useTranslation();
  const [cguChecked, setCguChecked] = useState(false);
  const [cgvChecked, setCgvChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const cguRequired = Boolean(legal?.cgu?.needs_acceptance);
  const cgvRequired = Boolean(legal?.cgv_required && legal?.cgv?.needs_acceptance);

  useEffect(() => {
    if (!open) return;
    setCguChecked(false);
    setCgvChecked(false);
    setSubmitting(false);
    setError(null);
  }, [open]);

  const canSubmit = useMemo(() => {
    if (!open) return false;
    if (cguRequired && !cguChecked) return false;
    if (cgvRequired && !cgvChecked) return false;
    return true;
  }, [open, cguRequired, cgvRequired, cguChecked, cgvChecked]);

  if (!open) return null;

  const accept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/legal/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cgu: Boolean(cguRequired ? cguChecked : true),
          cgv: Boolean(cgvRequired ? cgvChecked : true)
        })
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || 'Acceptance failed');
      }
      if (typeof onAccepted === 'function') await onAccepted();
    } catch (e) {
      setError(String(e?.message || e || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2">
            {t('legal.modal.title')}
          </h2>
          <p className="text-gray-700 mb-5">
            {t('legal.modal.subtitle')}
          </p>

          <div className="space-y-4 mb-6">
            {cguRequired ? (
              <label className="flex gap-3 items-start">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cguChecked}
                  onChange={(e) => setCguChecked(e.target.checked)}
                />
                <span className="text-gray-900 cursor-pointer">
                  {t('legal.modal.accept_cgu')}{' '}
                  <a className="text-blue-600 font-semibold hover:underline" href="/terms" target="_blank" rel="noreferrer">
                    {t('legal.modal.read_cgu')}
                  </a>
                  {legal?.cgu?.required_version ? (
                    <span className="text-gray-500"> ({t('legal.modal.version')}: {String(legal.cgu.required_version)})</span>
                  ) : null}
                </span>
              </label>
            ) : null}

            {cgvRequired ? (
              <label className="flex gap-3 items-start">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={cgvChecked}
                  onChange={(e) => setCgvChecked(e.target.checked)}
                />
                <span className="text-gray-900 cursor-pointer">
                  {t('legal.modal.accept_cgv')}{' '}
                  <a className="text-blue-600 font-semibold hover:underline" href="/terms/sales" target="_blank" rel="noreferrer">
                    {t('legal.modal.read_cgv')}
                  </a>
                  {legal?.cgv?.required_version ? (
                    <span className="text-gray-500"> ({t('legal.modal.version')}: {String(legal.cgv.required_version)})</span>
                  ) : null}
                </span>
              </label>
            ) : null}
          </div>

          <div className="text-sm text-gray-600 mb-5">
            <div className="font-bold text-gray-800 mb-1">{t('legal.modal.information')}</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <a className="text-blue-600 font-semibold hover:underline" href="/privacy" target="_blank" rel="noreferrer">
                  {t('legal.modal.privacy')}
                </a>
              </li>
              <li>
                <a className="text-blue-600 font-semibold hover:underline" href="/ai-data-usage" target="_blank" rel="noreferrer">
                  {t('legal.modal.ai_data_usage')}
                </a>
              </li>
            </ul>
          </div>

          {error ? (
            <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm font-semibold">
              {error}
            </div>
          ) : null}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 font-bold hover:bg-gray-50"
              onClick={() => (typeof onLogout === 'function' ? onLogout() : null)}
              disabled={submitting}
            >
              {t('legal.modal.logout')}
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white font-extrabold hover:bg-blue-700 disabled:bg-blue-300"
              onClick={accept}
              disabled={!canSubmit || submitting}
            >
              {submitting ? t('legal.modal.accepting') : t('legal.modal.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


